import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useRouter } from 'next/router';
import { MakeCustomValueType, Target } from 'framer-motion/types/types';
import { AnimatePresence, motion } from 'framer-motion';
import { SplitModal } from '@/core/modal';
import { useTracker } from '@/core/tracking';
import Spinner from '@/ui/content/Spinner';
import modalCover from '@/public/images/covers/pink-girl.webp';
import { isLoggedInSelector } from '@/stores/authStore';
import { closeModalSelector } from '@/stores/modalStore';

import { DeviceIds, deviceType } from '@/core/environment/deviceType';
import { PaymentForm, SubscriptionBenefits } from '@/features/payment';
import OfferPageModal, {
  OfferPageSelection,
} from '@/features/payment/offerPage/OfferPageModal';
import { getPaywallOfferPageVariant } from '@/features/payment/offerPage/paywallOfferPageVariant';
import PaymentStrategiesModal from '@/features/payment/paymentStrategies/PaymentStrategiesModal';
import { useConsent } from '@/features/userConsent';
import { UserAction, useUser } from '@/services/userService';
import settingsState from '@/stores/settingsStore';
import {
  usePaymentService,
  usePriceService,
} from '@/layout/appWrapper/ServiceProvider';
import { isFreeTrialEnabledState } from '@/stores/onboardingStore';
import LoginForm from '../account/login/LoginForm';
import { LoginBenefits } from '../account/login';

const fadeHidden: MakeCustomValueType<Target> = {
  opacity: 0,
  position: 'absolute',
};
const fadeVisible: MakeCustomValueType<Target> = {
  opacity: 1,
  position: 'relative',
};

const slideHidden = { x: 100, ...fadeHidden };
const slideVisible = { x: 0, ...fadeVisible };

type Props = {
  previousAction?: UserAction;
  id: string;
  onClose?: () => void;
  forceLogin?: boolean;
  stepIndex?: string;
  loginOnly?: boolean;
  loginPreviousAction?: string;
  onReturnToOfferPage?: () => void;
  onReturnToOfferPageFromCheckout?: () => void;
  isReturningToOfferPageRef?: MutableRefObject<boolean>;
  initialOfferPageStage?: 'other-plans' | 'checkout';
  initialOfferPageSelection?: OfferPageSelection;
  isUpgrade?: boolean;
};

export interface Step {
  name: 'login' | 'paywall';
  guard?: () => Promise<boolean | undefined>;
  Aside: typeof LoginBenefits | typeof SubscriptionBenefits;
  Form: typeof LoginForm | typeof PaymentForm;
}

export default function OnboardingModal({
  previousAction,
  loginPreviousAction,
  id,
  onClose,
  forceLogin,
  loginOnly,
  onReturnToOfferPage,
  onReturnToOfferPageFromCheckout,
  isReturningToOfferPageRef,
  initialOfferPageStage,
  initialOfferPageSelection,
  isUpgrade = false,
  ...rest
}: Props) {
  const closeModal = useSetRecoilState(closeModalSelector);
  const isLoggedIn = useRecoilValue(isLoggedInSelector);
  const liveIsFreeTrialEnabled = useRecoilValue(isFreeTrialEnabledState);
  const { data: user, isLoading: isUserLoading } = useUser();
  const { usePaywallPrices, getPricesSeenByUser } = usePriceService();
  const paywallPrices = usePaywallPrices();
  const router = useRouter();
  const trackedStepsRef = useRef<Set<Step['name']>>(new Set());
  const paymentFormScrollRef = useRef({ el: null, scrolled: false });
  const { track, setCategoryParams } = useTracker();
  const { shouldShowMarketingConsentModal } = useConsent();
  const [stepIndex, setStepIndex] = useState(isLoggedIn && !forceLogin ? 1 : 0);
  const [nextStepIndex, setNextStepIndex] = useState(0);
  const settings = useRecoilValue(settingsState);
  const [offerPageStage, setOfferPageStage] = useState<
    'offer' | 'other-plans' | 'checkout'
  >(initialOfferPageStage || 'offer');
  const [offerPageSelection, setOfferPageSelection] = useState<
    OfferPageSelection | undefined
  >(initialOfferPageSelection);
  const hasTrackedOfferPageRef = useRef(false);
  const offerPageVariant = getPaywallOfferPageVariant(
    settings.values.paywallOfferPageVariant
  );
  const isOcpOfferPageReady =
    offerPageVariant !== 'ocp' ||
    Boolean(
      settings.values.ocp2wIntroPriceId && user?.monetization?.trialAvailable
    );
  const isOcpOfferPagePending =
    offerPageVariant === 'ocp' && offerPageStage === 'offer' && isUserLoading;
  const isDesktop = useMemo(() => deviceType() === DeviceIds.Desktop, []);
  const { isBaseUser } = usePaymentService();
  const steps: Step[] = useMemo(
    () => [
      {
        name: 'login',
        Aside: LoginBenefits,
        Form: LoginForm,
      },
      {
        name: 'paywall',
        // I could move the guard to the component itself, but that would force me to
        // move the tracker calls (e.g. 'paywall_shown') to the component too. Besides,
        // the SubscriptionForm component is enough difficult to read as it is. It's
        // best not to add stuff in there.
        guard: async () => {
          if (!user && isUserLoading && loginPreviousAction) return undefined;

          return !!user && isBaseUser(user) && !router.query.redeemCode;
        },
        Aside: SubscriptionBenefits,
        Form: PaymentForm,
      },
    ],
    [
      user,
      isUserLoading,
      loginPreviousAction,
      isBaseUser,
      router.query.redeemCode,
    ]
  );

  const Step = useMemo(
    () => steps[rest.stepIndex || stepIndex],
    [steps, stepIndex, rest.stepIndex]
  );

  const priceIds = useMemo(() => {
    if (Step.name !== 'paywall' || !paywallPrices) return undefined;

    return getPricesSeenByUser(paywallPrices);
  }, [Step.name, getPricesSeenByUser, paywallPrices]);
  const shouldShowOfferPage =
    Step.name === 'paywall' &&
    !isUpgrade &&
    offerPageVariant !== 'control' &&
    isOcpOfferPageReady &&
    !isOcpOfferPagePending &&
    offerPageStage === 'offer';
  const shouldSkipPaywallShownTracking =
    Step.name === 'paywall' && Boolean(initialOfferPageStage);

  const returnToOfferPage = useCallback(() => {
    if (onReturnToOfferPage) {
      closeModal(id);
      onReturnToOfferPage();
      return;
    }

    if (offerPageStage !== 'offer') {
      setOfferPageStage('offer');
    }
  }, [closeModal, id, offerPageStage, onReturnToOfferPage]);

  const returnToOfferPageFromCheckout = useCallback(() => {
    if (onReturnToOfferPageFromCheckout) {
      closeModal(id);
      onReturnToOfferPageFromCheckout();
      return;
    }

    if (offerPageStage !== 'offer') {
      setOfferPageStage('offer');
    }
  }, [closeModal, id, offerPageStage, onReturnToOfferPageFromCheckout]);

  useEffect(() => {
    if (trackedStepsRef.current.has(Step.name)) return;
    if (shouldShowOfferPage || shouldSkipPaywallShownTracking) {
      trackedStepsRef.current.add(Step.name);
      return;
    }
    if (isOcpOfferPagePending) return; // Wait for user data before deciding which paywall to track
    if (Step.name === 'paywall' && !priceIds) return; // Track paywall only after prices infos have loaded

    track(Step.name, 'shown', {
      previousAction,
      priceIds,
      ...(Step.name === 'paywall' ? { type: 'old' } : {}),
    });
    trackedStepsRef.current.add(Step.name);
  }, [
    Step.name,
    track,
    previousAction,
    priceIds,
    shouldShowOfferPage,
    shouldSkipPaywallShownTracking,
    isOcpOfferPagePending,
  ]);

  const proceedToStep = useCallback(
    (nextStep: number) => {
      if (nextStep < steps.length && !(Step.name === 'login' && loginOnly)) {
        setNextStepIndex(nextStep);
      } else {
        closeModal(id);
      }
    },
    [steps, closeModal, id, loginOnly, Step.name]
  );

  useEffect(() => {
    if (nextStepIndex > stepIndex) {
      const { guard } = steps[nextStepIndex];

      if (typeof guard !== 'function') {
        return setStepIndex(nextStepIndex);
      }

      const waitForGuard = async () => {
        const canProceed = await guard();
        if (canProceed === undefined) return;

        if (canProceed) {
          setStepIndex(nextStepIndex);
        } else {
          // Skip the step
          proceedToStep(nextStepIndex + 1);
        }
      };

      waitForGuard();
    }
  }, [nextStepIndex, stepIndex, proceedToStep, steps]);

  const onModalClose = useCallback(async () => {
    if (isReturningToOfferPageRef?.current) return;

    track(Step.name, 'dismissed', { priceIds });
    if (
      offerPageVariant !== 'control' &&
      !isUpgrade &&
      offerPageStage !== 'offer'
    ) {
      track(
        offerPageVariant,
        'dismissed',
        offerPageVariant === 'pcp'
          ? { freeTrial: liveIsFreeTrialEnabled }
          : undefined
      );
    }
    if (typeof onClose === 'function') onClose();

    if (Step.name === 'paywall') {
      // Show consent modal if user has been created from less than 60 seconds
      const isUserCreatedFromLessThan60sec =
        user && Math.abs(new Date().getTime() - +user.createdAt) < 60000;

      if (user?.createdAt && isUserCreatedFromLessThan60sec) {
        shouldShowMarketingConsentModal();
      }
    }
  }, [
    Step.name,
    track,
    onClose,
    priceIds,
    shouldShowMarketingConsentModal,
    user,
    isReturningToOfferPageRef,
    offerPageVariant,
    isUpgrade,
    offerPageStage,
    liveIsFreeTrialEnabled,
  ]);

  // Yes, I'm lazy and I want to avoid any prop drilling
  useEffect(() => {
    const affectedCategories = ['login', 'paywall', 'payment', 'free_trial'];
    for (const category of affectedCategories) {
      setCategoryParams(category, (categoryParams) => ({
        ...categoryParams,
        previousAction:
          category === 'login'
            ? loginPreviousAction || previousAction
            : previousAction,
      }));
    }
    return () => {
      for (const category of affectedCategories) {
        setCategoryParams(category, ({ previousAction, ...rest }) => rest);
      }
    };
  }, [setCategoryParams, previousAction, loginPreviousAction]);

  const enablePaymentWithTrial = useMemo(
    () =>
      Step.name === 'paywall' &&
      previousAction === UserAction.FreeTrial &&
      settings.values.shouldForceHomeFreeTrial,
    [user, settings.values.shouldForceHomeFreeTrial, previousAction]
  );

  return shouldShowOfferPage ? (
    <OfferPageModal
      id={id}
      variant={offerPageVariant}
      shouldTrackShown={!hasTrackedOfferPageRef.current}
      onShown={() => {
        hasTrackedOfferPageRef.current = true;
      }}
      previousAction={loginPreviousAction || previousAction}
      initialSelection={offerPageSelection}
      onContinue={(selection) => {
        setOfferPageSelection(selection);
        setOfferPageStage('checkout');
      }}
      onOtherPlans={(selection) => {
        setOfferPageSelection(selection);
        setOfferPageStage('other-plans');
      }}
      onDismiss={onModalClose}
    />
  ) : Step.name === 'paywall' ? (
    isOcpOfferPagePending ? (
      <Spinner inline={false} />
    ) : (
      <PaymentStrategiesModal
        onSubmit={() => proceedToStep(stepIndex + 1)}
        id={id}
        onClose={onModalClose}
        isUpgrade={isUpgrade}
        autoEnableTrial={enablePaymentWithTrial}
        onBackToOfferPage={
          offerPageStage !== 'offer' || onReturnToOfferPage
            ? returnToOfferPage
            : undefined
        }
        onBackFromCheckout={
          offerPageStage === 'checkout' || onReturnToOfferPageFromCheckout
            ? returnToOfferPageFromCheckout
            : undefined
        }
        {...rest}
      />
    )
  ) : (
    <SplitModal
      className={`max-w-lg ${
        Step.name === 'paywall'
          ? 'md:max-w-3.5xl'
          : 'md:max-w-3xl md:min-h-[612px]'
      } min-h-[420px]`}
      label="Get started"
      id={id}
      onClose={onModalClose}
      {...rest}
    >
      <SplitModal.LeftPanel
        mobileBackground={modalCover}
        desktopBackground={modalCover}
        className="lg:min-w-100 lg:w-100 lg:!pe-11"
        rootChildren={<div className="absolute top-0 start-0 w-full h-full" />}
      >
        <div className="relative w-full">
          <AnimatePresence initial={false}>
            <motion.div
              className="w-full start-0 bottom-0"
              key={Step.name}
              initial={slideHidden}
              animate={slideVisible}
              exit={slideHidden}
            >
              {isDesktop && <Step.Aside />}
            </motion.div>
          </AnimatePresence>
        </div>
      </SplitModal.LeftPanel>
      <SplitModal.RightPanel
        className={`overflow-y-auto ${
          Step.name === 'paywall'
            ? 'lg:!px-17 md:short:!py-8'
            : 'flex flex-col justify-center'
        }`}
        sectionRef={(ref) => {
          paymentFormScrollRef.current.el = ref;
        }}
      >
        <div className="relative w-full">
          <AnimatePresence initial={false} exitBeforeEnter>
            <motion.div
              className="w-full top-0 start-0"
              key={Step.name}
              initial={fadeHidden}
              animate={fadeVisible}
              exit={fadeVisible}
            >
              <Step.Form onSubmit={() => proceedToStep(stepIndex + 1)} />
            </motion.div>
          </AnimatePresence>
        </div>
      </SplitModal.RightPanel>
    </SplitModal>
  );
}
